import {useMemo} from 'react';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import type {CampaignTab} from '@/types/campaignTab';
import {CAMPAIGN_TAB_ID, type CampaignTabId} from '@/types/campaignTabId';

const TAB_TEMPLATES = [
  {
    id: CAMPAIGN_TAB_ID.CREATORS,
    pathSuffix: '/creators',
    localeLabelKey: 'Creators',
    testID: 'creators',
    order: 1,
  },
  {
    id: CAMPAIGN_TAB_ID.ASSETS,
    pathSuffix: '/assets',
    localeLabelKey: 'Assets',
    testID: 'assets',
    order: 2,
  },
  {
    id: CAMPAIGN_TAB_ID.STATISTICS,
    pathSuffix: '/statistics',
    localeLabelKey: 'Statistics',
    testID: 'statistics',
    order: 3,
  },
] as const;

type VisibilityCtx = {hasSocialNetwork: boolean};
type TabVisibilityPolicy = Partial<
  Record<CampaignTabId, (ctx: VisibilityCtx) => boolean>
>;

const defaultVisibility: TabVisibilityPolicy = {};

interface UseCampaignTabsV2Params {
  campaignId: string | undefined;
  hasSocialNetwork: boolean;
  visibility?: TabVisibilityPolicy;
}

function useCampaignTabsV2({
  campaignId,
  hasSocialNetwork,
  visibility = defaultVisibility,
}: UseCampaignTabsV2Params): {
  tabs: CampaignTab[];
} {
  return useMemo(() => {
    if (!campaignId) {
      return {tabs: []};
    }

    const basePath = `${ROUTES.CAMPAIGNS_V2}/${campaignId}`;
    const ctx = {hasSocialNetwork};

    const tabs: CampaignTab[] = TAB_TEMPLATES.filter(
      (t) => visibility[t.id]?.(ctx) ?? true
    ).map((t) => ({
      id: t.id,
      path: `${basePath}${t.pathSuffix}`,
      label: translate(t.localeLabelKey),
      testID: t.testID,
      order: t.order,
    }));

    return {tabs};
  }, [campaignId, hasSocialNetwork, visibility]);
}

export default useCampaignTabsV2;
